/**
 * This utility function calculates the discount amount between a new price and an original price, while ensuring type safety.
 * Use only in Google Analytics ecommerce tracking contexts.
 */
export const calculateGADiscountAmount = (params: {
  newPrice: number | null | undefined | string
  originalPrice: number | null | undefined | string
}): number => {
  const { newPrice, originalPrice } = params
  return typeof newPrice === 'number' &&
    typeof originalPrice === 'number' &&
    originalPrice > newPrice
    ? originalPrice - newPrice
    : 0
}

/**
 * CLIENT REQUEST: copy of `event` with the agreed keys removed from the
 * `ecommerce` object and from every object in `ecommerce.items[]`
 * (staging-only, see `ga4.stripItemParams`). At the ecommerce level only
 * `affiliation` and `item_list_id` actually occur; the rest are no-ops there.
 * Non-mutating: item objects are reused across events and re-validated by zod.
 * Non-object items (promotion events push strings) and events without an
 * `ecommerce` object pass through untouched.
 */
export const stripGAItemParams = <T extends Record<string, any>>(
  event: T
): T => {
  if (!event?.ecommerce || typeof event.ecommerce !== 'object') return event
  /* eslint-disable @typescript-eslint/no-unused-vars -- the named keys are the strip list */
  const strip = ({
    affiliation,
    index,
    item_brand,
    item_category,
    item_category2,
    item_category4,
    item_img,
    item_list_id,
    item_mfc_id,
    item_name,
    item_variant,
    ...kept
  }: Record<string, any>) => kept
  /* eslint-enable @typescript-eslint/no-unused-vars */
  const { items, ...rest } = event.ecommerce
  const ecommerce: Record<string, any> = strip(rest)
  if (items !== undefined) {
    ecommerce.items = Array.isArray(items)
      ? items.map(item =>
          item && typeof item === 'object' ? strip(item) : item
        )
      : items
  }
  return { ...event, ecommerce }
}
